import { Circle, Loader2, X } from "lucide-react";

export const mono = "'JetBrains Mono', 'Fira Code', monospace";

export const inputStyle = {
  background: "var(--bg-field)",
  border: "1px solid var(--border-field)",
  color: "var(--text-main)",
  borderRadius: "6px",
  padding: "8px 12px",
  fontSize: "12px",
  fontFamily: mono,
  width: "100%",
  outline: "none",
  boxSizing: "border-box",
};

export const labelStyle = {
  color: "var(--text-muted)",
  fontSize: "10px",
  fontFamily: mono,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  display: "block",
  marginBottom: "5px",
};

export const buttonBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  borderRadius: "6px",
  padding: "7px 14px",
  fontSize: "12px",
  fontFamily: mono,
  fontWeight: 700,
  cursor: "pointer",
  border: "none",
  letterSpacing: "0.04em",
};

export const statusConfig = {
  ACTIVE: { color: "#00e676", bg: "rgba(0,230,118,0.10)", border: "rgba(0,230,118,0.25)" },
  INACTIVE: { color: "#8b949e", bg: "rgba(139,148,158,0.10)", border: "rgba(139,148,158,0.22)" },
  RUNNING: { color: "#00e676", bg: "rgba(0,230,118,0.10)", border: "rgba(0,230,118,0.25)" },
  STOPPED: { color: "#ffd740", bg: "rgba(255,215,64,0.10)", border: "rgba(255,215,64,0.25)" },
  FILLED: { color: "#00b0ff", bg: "rgba(0,176,255,0.10)", border: "rgba(0,176,255,0.25)" },
  OPEN: { color: "#ffd740", bg: "rgba(255,215,64,0.10)", border: "rgba(255,215,64,0.25)" },
  CLOSED: { color: "#546e7a", bg: "rgba(84,110,122,0.10)", border: "rgba(84,110,122,0.25)" },
  CANCELLED: { color: "#ff5252", bg: "rgba(255,82,82,0.10)", border: "rgba(255,82,82,0.25)" },
};

export function getStatusConfig(status) {
  return statusConfig[status] || {
    color: "#8b949e",
    bg: "rgba(139,148,158,0.10)",
    border: "rgba(139,148,158,0.25)",
  };
}

export function StatusBadge({ status, label = status, title }) {
  const cfg = getStatusConfig(status);

  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: "4px",
        padding: "2px 9px",
        fontSize: "10px",
        fontWeight: 700,
        fontFamily: mono,
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
        cursor: title ? "help" : "default",
      }}
    >
      <Circle size={5} fill={cfg.color} color={cfg.color} />
      {label}
    </span>
  );
}

export function Skeleton({ width = "100%", height = "14px", radius = "4px" }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: "linear-gradient(90deg, var(--skeleton-base) 25%, var(--skeleton-shimmer) 50%, var(--skeleton-base) 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.4s infinite",
      }}
    />
  );
}

export function StatChip({ label, value, color = "var(--text-muted)" }) {
  return (
    <span
      style={{
        background: "var(--bg-field)",
        border: "1px solid var(--border-field)",
        borderRadius: "6px",
        padding: "3px 10px",
        fontSize: "11px",
        color: "var(--text-muted)",
        fontFamily: mono,
      }}
    >
      {label}: <span style={{ color, fontWeight: 700 }}>{value}</span>
    </span>
  );
}

export function StatCard({ label, value, sub, icon: Icon, accentColor, loading, badge, valueColor }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "20px 22px",
        borderLeft: `3px solid ${accentColor}`,
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        transition: "border-color 0.2s",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <span style={{ color: "var(--text-muted)", fontSize: "10px", fontFamily: mono, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {label}
        </span>
        <div
          style={{
            background: `${accentColor}15`,
            border: `1px solid ${accentColor}30`,
            borderRadius: "8px",
            padding: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={15} color={accentColor} />
        </div>
      </div>

      {loading ? (
        <>
          <Skeleton width="60px" height="32px" />
          <Skeleton width="120px" height="12px" radius="3px" />
        </>
      ) : (
        <>
          <span style={{ color: valueColor || "var(--text-main)", fontSize: "30px", fontWeight: 800, fontFamily: mono, lineHeight: 1 }}>
            {value}
          </span>
          {badge && <StatusBadge status={badge} label={badge} />}
          {sub && <span style={{ color: "var(--text-muted)", fontSize: "11px", fontFamily: mono }}>{sub}</span>}
        </>
      )}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  );
}

export function IconButton({ onClick, title, icon, danger = false, disabled = false, accentColor, solid = false }) {
  const color = danger ? "#ff5252" : accentColor || "#8b949e";
  const baseBg = solid ? color : danger ? "rgba(255,82,82,0.10)" : `${color}18`;
  const hoverBg = solid ? color : danger ? "rgba(255,82,82,0.20)" : `${color}30`;

  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        background: baseBg,
        border: `1px solid ${danger ? "rgba(255,82,82,0.25)" : `${color}40`}`,
        color: solid ? "#fff" : color,
        borderRadius: "6px",
        padding: "6px",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
        transition: "background 0.15s, color 0.15s",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = hoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = baseBg;
      }}
    >
      {icon}
    </button>
  );
}

export function Modal({ onClose, children, title, subtitle, wide = false, maxWidth }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(6px)",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-field)",
          borderRadius: "14px",
          width: "100%",
          maxWidth: maxWidth || (wide ? "800px" : "520px"),
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "18px 20px 14px",
            borderBottom: "1px solid var(--border-color)",
            position: "sticky",
            top: 0,
            background: "var(--bg-card)",
            zIndex: 5,
          }}
        >
          <div>
            <h3 style={{ color: "var(--text-main)", fontSize: "15px", fontWeight: 800, margin: 0, fontFamily: mono }}>{title}</h3>
            {subtitle && <p style={{ color: "var(--text-muted)", fontSize: "11px", margin: "4px 0 0", fontFamily: mono }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: "2px" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: "20px" }}>{children}</div>
      </div>
    </div>
  );
}

export function LoadingState({ message, color = "#388bfd", height = "220px" }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        gap: "12px",
      }}
    >
      <Loader2 size={24} color={color} style={{ animation: "spin 1s linear infinite" }} />
      <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: 0, letterSpacing: "0.06em", fontFamily: mono }}>{message}</p>
    </div>
  );
}
